import { List, fromJS } from 'immutable';
import { GaugeVisualizationRecord } from 'reporting-visualizations/visualizations/gauge/GaugeRecords';
import { getPrimaryDataDataset, getPrimaryDataset, getCompareDataDataset } from 'reporting-data/v2/dataset/datasetGetters';
import { VisualizationTypes } from 'reporting-data/tsTypes/visualization';
import { isChartTypeColorPalette, isChartTypeColorTheme } from 'reporting-data/tsTypes/reportChart/color';
import { isBrandKitTheme } from '../../../../reports/utils/colors/brandKitUtils';
import { getPaletteColorScheme } from './colorUtils';
//@ts-expect-error Untyped import
import { pickTokenizedColors } from '../../../../reports/utils/colors/colorPicker';
import { getColorPalette } from 'reporting-snowflake/relational/visualize/color/utils';
import { ColorOption } from 'reporting-snowflake/relational/schema/highcharts-settings';
import { PaletteIds, THEME } from 'reporting-data/tsTypes/visualization/colors';
import { updateComparisonDatasetPropertyLabels } from '../../../../reports/utils/inferUtils';
import { getDiscreteColors } from 'reporting-snowflake/relational/visualize/color/assign';
import { DefaultCategorical, getDeprecatedGaugePalettes } from 'reporting-snowflake/relational/visualize/color/palettes';
import { getStaticLegacyThemes } from 'reporting-visualizations/color/themes';
const DEFAULT_GAUGE_COLOR_OPTIONS = {
  paletteId: PaletteIds.Categorical14
};
const mapGaugeBandsToColors = ({
  bands = [],
  palette = DefaultCategorical
}) => {
  const discreteColors = getDiscreteColors(fromJS(palette).get('scheme'), bands.length);
  return bands.reduce((acc, band, i) => {
    acc[i] = band['color'] || discreteColors.get(i % discreteColors.size);
    return acc;
  }, {});
};
const applyPaletteToBands = ({
  bands,
  color = {},
  brandKitPalettes,
  palettes
}) => {
  const {
    GAUGE_PALETTES
  } = palettes || {
    GAUGE_PALETTES: getDeprecatedGaugePalettes('gauge-visualization-fallback') // Temporary for backwards compatibility as we migrate to the new useReportingPalettes hook
  };
  const palette = getColorPalette(ColorOption(color),
  // TODO: decouple from snowflake
  brandKitPalettes,
  // TODO: fix this type
  GAUGE_PALETTES, palettes === null || palettes === void 0 ? void 0 : palettes.DefaultCategorical);
  const mappedBands = mapGaugeBandsToColors({
    bands,
    palette
  });
  return bands.map((band, i) => {
    return Object.assign({}, band, {
      color: mappedBands[i]
    });
  });
};
const shouldReverseThemeColors = theme => [THEME.ORANGES, THEME.AQUAS, THEME.PURPLES, THEME.YELLOWS, THEME.PINKS, THEME.BLUES, THEME.GREENS].includes(theme);
const applyThemeToBands = ({
  bands,
  color = {},
  reportingThemes,
  brandKitPalettes
}) => {
  const {
    theme: themeID
  } = color;
  if (!themeID) {
    return bands;
  }
  const isBrandKitPalette = isBrandKitTheme(themeID);
  const theme = getPaletteColorScheme({
    colorConfig: fromJS(color),
    brandKitPalettes,
    reportingThemes
  });
  const shouldReverseColors = shouldReverseThemeColors(themeID); // This ensures gradient themes visually match interpolated palettes
  const bandIndices = bands.map((_, i) => i);
  const mappedColors = pickTokenizedColors({
    colorConfig: fromJS(color),
    keys: fromJS(bandIndices),
    reverseGradient: shouldReverseColors,
    customTheme: isBrandKitPalette && theme || undefined,
    reportingThemes
  }).toJS();
  return bands.map((band, i) => {
    return Object.assign({
      color: mappedColors[i]
    }, band);
  });
};
const applyColorsToBands = ({
  bands,
  color = DEFAULT_GAUGE_COLOR_OPTIONS,
  reportingThemes,
  brandKitPalettes,
  palettes
}) => {
  if (isChartTypeColorPalette(color)) {
    return applyPaletteToBands({
      bands,
      color,
      brandKitPalettes,
      palettes
    });
  } else if (isChartTypeColorTheme(color)) {
    return applyThemeToBands({
      bands,
      color,
      reportingThemes,
      brandKitPalettes
    });
  }
  return applyPaletteToBands({
    bands,
    color: DEFAULT_GAUGE_COLOR_OPTIONS,
    brandKitPalettes,
    palettes
  });
};

// TODO: the props tyep here we can make it reusable for other build visualization functions
const buildGaugeVisualization = ({
  datasetMap,
  gaugeReportChart,
  reportDetail,
  brandKitPalettes,
  originalReport,
  palettes,
  reportingThemes
}) => {
  var _reportingThemes;
  reportingThemes = (_reportingThemes = reportingThemes) !== null && _reportingThemes !== void 0 ? _reportingThemes : getStaticLegacyThemes('reporting-ui-components.buildGaugeVisualization');
  const primaryDataDataset = getPrimaryDataDataset(datasetMap);
  const visualizationPrimaryData = List.isList(primaryDataDataset) ? getPrimaryDataset(datasetMap) : primaryDataDataset;
  const {
    metrics,
    compareField,
    compare
  } = reportDetail;
  const {
    layer,
    style,
    color
  } = gaugeReportChart;
  const compareDataset = getCompareDataDataset(datasetMap);
  const updatedComparedDataset = compareDataset && compare && updateComparisonDatasetPropertyLabels({
    report: originalReport,
    metrics: reportDetail.metrics,
    compareDataset: compareDataset,
    primaryDataset: primaryDataDataset
  });
  const {
    plot,
    showRangeLabels,
    dataColumn,
    referenceLineValues = [],
    valueFormattingOptions
  } = layer || {};
  const {
    legendPosition,
    legendWidth
  } = style || {};
  const {
    min,
    max,
    bands = []
  } = plot || {};
  const result = GaugeVisualizationRecord({
    type: VisualizationTypes.GAUGE,
    data: fromJS(Object.assign({
      primary: visualizationPrimaryData
    }, updatedComparedDataset ? {
      compare: updatedComparedDataset
    } : null)),
    layer: {
      dataset: 'primary',
      // should be able to override from the report JSON eventually
      dataColumn: dataColumn || (metrics === null || metrics === void 0 ? void 0 : metrics[0]) || '',
      // the fallback here is just to workaround typescript issue right now. Refactor probably necessary.
      compareColumn: compareField,
      plot: {
        min,
        max,
        bands: applyColorsToBands({
          bands,
          color,
          reportingThemes,
          brandKitPalettes,
          palettes
        })
      },
      showRangeLabels,
      referenceLineValues,
      valueFormattingOptions
    },
    style: {
      legendPosition,
      legendWidth
    }
  });
  return result;
};
export default buildGaugeVisualization;